import type { AgencyAlertKind } from './agencyDigest';

/**
 * Every number that can move a client between colours or change what an outcome metric claims.
 *
 * Collected here rather than left beside the code that reads them because together they are one
 * editorial position, not independent implementation details: between them they decide how
 * much evidence the tab demands before it will say anything about a person. Spread across the
 * files that consume them, a reader can change one without seeing what the others already
 * guarantee, and nothing fails.
 *
 * Each value is pinned by a test in agencyThresholds.tests.ts, written against independent
 * literals rather than against these constants. Changing a value here fails those tests on
 * purpose: the failure is the prompt to record what moved.
 *
 * One exception, stated because a reader would otherwise trust the sentence above too far.
 * MIN_BASELINE_FINDINGS_FOR_STANDING is implied by MIN_BASELINE_WEEKS_FOR_STANDING at their current
 * values, so it is bracketed from above only and can be lowered without failing anything. The
 * reasoning is written out where the two are tested.
 *
 * What none of this establishes is whether these values are RIGHT for the distribution of real
 * clients. That needs reading an actual caseload rather than a fixture, and it is a separate
 * judgement from keeping them visible and pinned, which is all this file does.
 */

/**
 * The digest reads a 12-week window split 8/4: the older eight weeks establish what is normal for a
 * client, the most recent four are judged against it. Four weeks is short enough that a change shows
 * up while it still matters and long enough that a single quiet week does not settle a standing.
 *
 * The split falls on the week grid the chart draws, and the standing reads the older part in spans
 * as long as the recent part has run (earlierSpans in agencyDigest.ts). The current week is still
 * running, so recent rates divide by the weeks actually elapsed rather than by four, which keeps a
 * Monday from reading as a collapse in activity.
 */
export const DEFAULT_WINDOW_WEEKS = 12;
export const RECENT_WEEKS = 4;

/**
 * Whole rolling seven-day weeks that always fit in the recent part of the window. That part starts
 * on a bucket boundary, so it is three to four weeks long depending on the weekday, and three whole
 * weeks always fit. Derived rather than chosen, so RECENT_WEEKS's pin covers it.
 */
export const RECENT_ROLLING_WEEKS = RECENT_WEEKS - 1;

/**
 * How seriously a finding about the client reads, by its kind. The standing adds up the most serious
 * thing raised about each part of daily life, so these decide how far one finding can move it.
 *
 * Something that already happened outranks something that stood out, which outranks a drift or a
 * suggestion: the order the tab already ranks them in everywhere else. Three steps and no finer,
 * because the kind is all a finding states about how serious it is.
 */
export const SEVERITY_BY_KIND: { [key in AgencyAlertKind]: number } = {
    incident: 3,
    baseline: 2,
    observation: 1
};

/**
 * A finding the model was not confident about counts one step less. The reflection prompt keeps a
 * pattern built only from routine good news at low confidence, so this is the nearest the record
 * comes to telling a "keep it up" from a concern: a low-confidence drift counts for nothing.
 */
export const LOW_CONFIDENCE_DISCOUNT = 1;

/**
 * How far a client's recent reading may sit from their usual one before the standing moves.
 *
 * One step either way is absorbed. A single drift turning up in a part of life that had none, or one
 * part going quiet, is the ordinary churn of a sparse record. It takes something that stood out in a
 * new part of life, or two parts moving, to move a client.
 */
export const CONDITION_TOLERANCE = 1;

/**
 * A client also needs at least this many findings about them in the recent weeks before they can read
 * as declining. One finding is not a change of direction, however seriously it reads: something that
 * happened has its own route to the attention list, and anything else can wait for a second sighting.
 *
 * It guards the rising direction only. The falling direction has a guard of its own: a client with
 * nothing recorded in the recent weeks cannot settle, because a quiet record is not a calm client.
 */
export const MIN_RECENT_FINDINGS_TO_FLAG = 2;

/**
 * Findings about the client needed in the earlier weeks the standing reads (earlierSpans in
 * agencyDigest.ts) before the tab will state a standing for them at all. Findings about the
 * arrangements around them (bookings, refills) do not count.
 *
 * A standing is a comparison against this client's own earlier normal, so a client who has not
 * accumulated one has no standing to report. Without this floor the arithmetic still produces an
 * answer: no earlier findings and none recently gives a change of zero, which lands in the
 * tolerance band and renders the client as steady, asserting they are sitting at their usual level
 * when nothing whatever is known about their usual level. On a young caseload that is most of the
 * map, and it is the most confident-looking cell on the tab.
 *
 * Two mirrors MIN_RECENT_FINDINGS_TO_FLAG at the other end of the window and for the same reason:
 * one observation is a data point, not a normal.
 *
 * It withholds the REASSURING readings only. A client below the floor who is nonetheless rising
 * still reports as declining, because the two mistakes do not cost the same: a client wrongly
 * flagged costs a visit that was not needed, and a client wrongly shown as settled is one nobody
 * goes to see. So the floor governs what the tab is allowed to call calm, not what it is allowed
 * to raise.
 *
 * A newly onboarded client who is not declining therefore reads as not-yet-known for roughly their
 * first month, which is how long it takes their earliest findings to age out of the recent span and
 * into the baseline half. Not eight weeks: eight is the length of the baseline half, not the wait
 * before anything lands in it.
 *
 * A client who never accumulates the evidence stays unknown rather than becoming measurable on a
 * timer, which is the honest reading and a different statement from a client who has history and
 * is genuinely quiet.
 */
export const MIN_BASELINE_FINDINGS_FOR_STANDING = 2;

/**
 * And spread across at least this many of those weeks, for the same reason the caseload-wide
 * trend needs MIN_WEEKS_WITH_FINDINGS: a pile of findings on one day is a single event being written
 * up, not a normal.
 *
 * Without it the count alone leaves the most reassuring reading on the tab the least guarded. Three
 * findings entered on one afternoon, then four quiet weeks, satisfies a count of two and renders a
 * green "settled" cell, which says this client came back down from a baseline that was never
 * established. The same burst flagged them as declining while it was recent, so the green arrives
 * purely because the findings aged.
 */
export const MIN_BASELINE_WEEKS_FOR_STANDING = 2;

/**
 * Below this the caseload has not accumulated enough history for a trend to mean anything, and each
 * panel that compares across weeks says what it is waiting for instead of drawing a shape out of
 * three data points.
 */
export const MIN_FINDINGS_FOR_TREND = 8;

/** And spread across at least this many weeks: a pile of findings in one week is not a trend. */
export const MIN_WEEKS_WITH_FINDINGS = 3;

/**
 * Clients an outcome rate needs behind it before it is shown as a percentage. Below this the card
 * shows the two counts instead, "4 of 6", and with nobody to count it shows no figure at all.
 *
 * Each client is ten points of a rate over ten, and more than that below it: one fall on a book of
 * four moves the figure 25 points, which reads on a slide as a collapse in care. The counts say the
 * same thing without implying a precision the base cannot carry. Ten is a judgement about where a
 * percentage stops misleading, not a statistical threshold.
 */
export const MIN_CLIENTS_FOR_A_RATE = 10;
